<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="72px">
      <el-form-item label="关键词">
        <el-input
          v-model="queryParams.keyword"
          class="!w-220px"
          clearable
          placeholder="名称 / 关键词"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="主分类">
        <el-select
          v-model="queryParams.activityKey"
          class="!w-160px"
          clearable
          filterable
          placeholder="全部"
        >
          <el-option
            v-for="cat in categoryOptions"
            :key="cat.id"
            :label="cat.name"
            :value="cat.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" class="!w-140px" clearable placeholder="全部">
          <el-option label="上架" :value="0" />
          <el-option label="下架" :value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" />重置</el-button>
        <el-button v-hasPermi="['market:template:create']" type="primary" @click="openCreate">
          <Icon icon="ep:plus" />新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="源站ID" prop="sourceId" width="90" />
      <el-table-column label="封面" width="90">
        <template #default="{ row }">
          <el-image
            v-if="row.coverUrl"
            :src="row.coverUrl"
            fit="cover"
            style="width: 56px; height: 56px; border-radius: 6px"
          />
        </template>
      </el-table-column>
      <el-table-column label="主分类" width="110">
        <template #default="{ row }">{{ categoryName(row.activityKey) }}</template>
      </el-table-column>
      <el-table-column label="名称" prop="name" min-width="160" show-overflow-tooltip />
      <el-table-column label="简介" prop="brief" min-width="200" show-overflow-tooltip />
      <el-table-column label="热度" prop="hotScore" width="90" />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-switch
            :model-value="row.status"
            :active-value="0"
            :inactive-value="1"
            @change="(v) => changeStatus(row, Number(v))"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button v-hasPermi="['market:template:update']" link type="primary" @click="openEdit(row.id)">
            编辑
          </el-button>
          <el-button
            v-hasPermi="['market:template:delete']"
            link
            type="danger"
            @click="handleDelete(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <TemplateForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import * as MarketTemplateApi from '@/api/market/template'
import * as CategoryApi from '@/api/market/category'
import type { MarketCategoryVO } from '@/api/market/category'
import TemplateForm from './TemplateForm.vue'

defineOptions({ name: 'MarketTemplate' })

const message = useMessage()
const formRef = ref<InstanceType<typeof TemplateForm>>()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const categoryOptions = ref<MarketCategoryVO[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  keyword: '',
  activityKey: undefined as number | undefined,
  status: undefined as number | undefined
})

const categoryName = (activityKey?: number) => {
  if (activityKey == null) return '-'
  return categoryOptions.value.find((c) => c.id === activityKey)?.name || `#${activityKey}`
}

const loadCategories = async () => {
  categoryOptions.value = await CategoryApi.getMarketCategoryList()
}

const getList = async () => {
  loading.value = true
  try {
    const data = await MarketTemplateApi.getMarketTemplatePage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } catch (e: any) {
    message.error(e?.message || '加载模板失败，请确认 yudao-server 已重启并包含 market 模块')
    list.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const openCreate = () => formRef.value?.open()
const openEdit = (id: number) => formRef.value?.open(id)

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.keyword = ''
  queryParams.activityKey = undefined
  queryParams.status = undefined
  handleQuery()
}

const changeStatus = async (row: any, status: number) => {
  await MarketTemplateApi.updateMarketTemplateStatus(row.id, status)
  row.status = status
  message.success('状态已更新')
}

const handleDelete = async (id: number) => {
  await message.delConfirm()
  await MarketTemplateApi.deleteMarketTemplate(id)
  message.success('删除成功')
  await getList()
}

onMounted(async () => {
  await loadCategories()
  await getList()
})
</script>
